import { TaxPropertyAttributeName } from './../propertyTypeEnum';
import { IAllAddresses, ICartAndActions, IExtendedSimpleProduct, IServiceAddress, StatusTypes } from '../types/buybox';
import getAddressRequestData from './get-address-request-data';
import { IDocumentSearchCriteria } from '../../../../customProxy/retailExtension/DataServiceEntities.g';
import { getDocumentAvailabilityAsync } from '../../../../customProxy/retailExtension/DataActionExtension.g';
import { getAttributeValuesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import checkDuplicateAtCart from './duplicate-address-at-cart';
import getAddressVerifiedData from './get-new-address-data';
import { v4 as uuid } from 'uuid';
import { format } from '@msdyn365-commerce-modules/utilities';
import { IBuyboxResources } from '../../definition-extensions/buybox.ext.props.autogenerated';
// import { DecryptData } from '../decryption-utility';
import { IActionGetDecryptionKeysData } from '../../../../actions/get-decryption-keys.action';

interface IAvailabilityResult {
    status: StatusTypes;
    correlationId?: string;
    message?: string;
    Data?: string;
}

interface IProductAndRequestData {
    product: IExtendedSimpleProduct;
    requestData: IDocumentSearchCriteria;
}

interface IProductAndResult extends IAvailabilityResult {
    product: IExtendedSimpleProduct;
    serviceAddress?: IServiceAddress;
}

const findExistingAddress = (allAddresses: IAllAddresses[], address: IServiceAddress) => {
    return allAddresses.find(
        addr =>
            addr.serviceAddress.suite === address.suite &&
            addr.serviceAddress.number === address.number &&
            addr.serviceAddress.streetName === address.streetName &&
            addr.serviceAddress.streetType === address.streetType &&
            addr.serviceAddress.quadrant === address.quadrant
    );
};

const getRequestDataForProducts = (
    products: IExtendedSimpleProduct[],
    cartAndActions: ICartAndActions
): Promise<IProductAndRequestData[]> => {
    const defaultRequestData = getAddressRequestData(cartAndActions);
    const { context } = cartAndActions;
    const { productTypePA } = context.app.config;
    const promises = products.map(async product => {
        const attributeValues = await getAttributeValuesAsync(
            { callerContext: context.actionContext, queryResultSettings: {} },
            product.RecordId,
            context.request.apiSettings.channelId,
            0
        );
        const productType = attributeValues.find(attr => attr.Name === productTypePA)?.TextValue;
        const requestData: IDocumentSearchCriteria = { ...defaultRequestData, ProductType: productType, CorrelationId: uuid() };
        return { product, requestData };
    });
    return Promise.all(promises);
};

const getDocumentResult = async (
    { product, requestData }: IProductAndRequestData,
    { context }: ICartAndActions,
    decryptionKeys: IActionGetDecryptionKeysData
): Promise<IProductAndResult> => {
    const result: IAvailabilityResult = {
        status: StatusTypes.Success,
        correlationId: requestData.CorrelationId
    };
    let serviceAddress: IServiceAddress | undefined;
    try {
        const response = await getDocumentAvailabilityAsync({ callerContext: context.actionContext }, requestData);
        if (response.IsSuccess) {
            if (!serviceAddress) {
                serviceAddress = JSON.parse(response.Address!) as IServiceAddress;
                serviceAddress.suite = serviceAddress.suite || '';
                result.Data = response.Data;
            }
            if (await checkDuplicateAtCart(serviceAddress, context, product.ItemId)) {
                result.status = StatusTypes.DuplicateInCart;
            }
        } else if (response.Status === '404') {
            result.status = StatusTypes.NotFound;
            result.message = response.Message;
        } else {
            result.status = StatusTypes.IntegrationError;
        }
    } catch (e) {
        const error = e as any;
        const status = error.status;
        if (status === 'Service Unavailable') {
            result.status = StatusTypes.Canceled;
        } else {
            result.status = StatusTypes.CRTError;
        }
    }
    // if (decryptionKeys.EncryptionEnable === 'true' && result?.Data) {
    //     const decryptData = DecryptData(result?.Data, decryptionKeys.EncryptionAesKey!, decryptionKeys.EncryptionAesIV!);
    //     console.log('DecryptData', decryptData);
    // }

    return { product: { ...product }, ...result, serviceAddress };
};

const alreadyExists = (product: IExtendedSimpleProduct, existingAddress?: IAllAddresses) => {
    return existingAddress?.relatedProduct.some(p => p.ItemId === product.ItemId);
};

const separateProducts = (results: IProductAndResult[], currentAddresses: IAllAddresses[]) => {
    const serviceAddress = results.find(result => result.serviceAddress)?.serviceAddress;
    const existingAddress = serviceAddress && findExistingAddress(currentAddresses, serviceAddress);
    const resultsWithStateDuplicates = results.map(result =>
        alreadyExists(result.product, existingAddress) ? { ...result, status: StatusTypes.DuplicateInState } : { ...result }
    );
    const successfulResults = resultsWithStateDuplicates.filter(result => result.status === StatusTypes.Success);
    const failedResults = resultsWithStateDuplicates.filter(result => result.status !== StatusTypes.Success);
    return { successfulResults, failedResults, serviceAddress, existingAddress };
};

const generateErrorMessages = (
    failedResults: IProductAndResult[],
    resources: IBuyboxResources,
    context: {
        [x: string]: any;
    }
) => {
    const {
        productNotFoundErrorMessage,
        duplicateInStateErrorMessage,
        duplicateInCartErrorMessage,
        somethingWentWrongErrorMessage,
        taxProductNotFoundErrorMessage
    } = resources;
    const errorMessages = failedResults.map(async result => {
        const name = result.product.Name;
        const { status, correlationId, message } = result;
        const attrResult = await getAttributeValuesAsync(
            { callerContext: context.actionContext, queryResultSettings: {} },
            result.product.RecordId,
            context.request.apiSettings.channelId,
            0
        );
        const checkIfTax = attrResult?.find(
            attr =>
                attr.TextValue?.toLowerCase() === TaxPropertyAttributeName.propertyTaxInformation.toLowerCase() ||
                attr.TextValue?.toLowerCase() === TaxPropertyAttributeName.propertyTaxStatementOfAccount.toLowerCase() ||
                attr.TextValue?.toLowerCase() === TaxPropertyAttributeName.propertyTaxCertificateRequestByEmail.toLowerCase()
        );
        const checkisBackendEndError = message?.includes('|PRODUCTNAME|');
        // replace |PRODUCTNAME| with product name
        const errorBackendMessage = checkisBackendEndError ? message?.replace('|PRODUCTNAME|', name || '') : `${name}: ${message}`;

        const errorMessage =
            errorBackendMessage && message
                ? errorBackendMessage
                : status === StatusTypes.NotFound
                ? checkIfTax
                    ? taxProductNotFoundErrorMessage
                    : productNotFoundErrorMessage
                : status === StatusTypes.DuplicateInState
                ? duplicateInStateErrorMessage
                : status === StatusTypes.DuplicateInCart
                ? duplicateInCartErrorMessage
                : somethingWentWrongErrorMessage;
        return format(errorMessage, name, status, correlationId);
    });
    return errorMessages;
};

const addProductOnAddress = async (
    products: IExtendedSimpleProduct[],
    cartAndActions: ICartAndActions,
    resources: IBuyboxResources,
    decryptionKeys: IActionGetDecryptionKeysData,
    displayErrorMessages: (msgs: string[]) => void
) => {
    const { allAddresses, setAllAddresses, clearValues, context } = cartAndActions;
    const allRequestData = await getRequestDataForProducts(products, cartAndActions);
    const requestResults = await Promise.all(allRequestData.map(data => getDocumentResult(data, cartAndActions, decryptionKeys)));
    const { successfulResults, failedResults, serviceAddress, existingAddress } = separateProducts(requestResults, allAddresses);
    if (successfulResults.length) {
        const productsToAdd = successfulResults.map(result => result.product);
        if (existingAddress) {
            existingAddress.relatedProduct.push(...productsToAdd);
        } else {
            const newAddress = getAddressVerifiedData(serviceAddress!, cartAndActions);
            newAddress.relatedProduct = productsToAdd;
            allAddresses.push(newAddress);
        }
        setAllAddresses([...allAddresses]);
    }
    const errorMessagesPromises = generateErrorMessages(failedResults, resources, context);
    const errorMessages = await Promise.all(errorMessagesPromises);
    displayErrorMessages(errorMessages);
    failedResults.some(result => result.status >= 5000) || clearValues();
};

export default addProductOnAddress;
